/**
 * CampaignHealthCard — the "ready?" signal.
 *
 * Deterministic readiness score with a clear label. Sits immediately
 * below the Executive Summary so the owner sees confidence *before*
 * they read any numbers.
 */
import { Card } from "@/shared/ui/card";
import { Progress } from "@/shared/ui/progress";
import { cn } from "@/shared/lib/utils";
import type { CampaignHealth } from "../../types";

export function CampaignHealthCard({ health }: { health: CampaignHealth }): JSX.Element {
  const dotColor =
    health.status === "ready"
      ? "text-emerald-500"
      : health.status === "ready_with_notes"
        ? "text-amber-500"
        : "text-rose-500";

  return (
    <section data-testid="strategy-campaign-health">
      <Card className="p-8">
        <div className="flex flex-col sm:flex-row sm:items-center gap-6">
          <div className="flex-1">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
              Campaign Health
            </p>
            <div className="flex items-baseline gap-3">
              <span className={cn("text-2xl", dotColor)} aria-hidden>
                {health.indicator}
              </span>
              <span
                className="text-2xl sm:text-3xl font-semibold"
                data-testid="strategy-health-label"
              >
                {health.label}
              </span>
            </div>
            {health.notes.length > 0 && (
              <ul
                className="mt-4 space-y-1.5 text-sm text-muted-foreground"
                data-testid="strategy-health-notes"
              >
                {health.notes.map((n, i) => (
                  <li key={i}>• {n}</li>
                ))}
              </ul>
            )}
          </div>
          <div className="sm:w-64">
            <div className="text-sm text-muted-foreground mb-2">Campaign Readiness</div>
            <div className="flex items-baseline gap-2">
              <span
                className="text-4xl font-semibold tracking-tight"
                data-testid="strategy-readiness-pct"
              >
                {health.readiness_pct}%
              </span>
            </div>
            <Progress value={health.readiness_pct} className="mt-3" />
          </div>
        </div>
      </Card>
    </section>
  );
}
